import React, { useState } from 'react';
import { useThemeStore } from '@core/theme/useThemeStore';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  isLoading?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  children,
  onClick,
  className = '',
  type = 'button',
  disabled = false,
  isLoading = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isDisabled = disabled || isLoading;
  // Should get the theme from useThemeStore
  const { theme } = useThemeStore();

  // Determine styles based on variant
  const getButtonStyles = () => {
    if (isDisabled) {
      // Disabled state for all variants
      if (variant === 'text') {
        return {
          color: theme?.subtleTextColor30,
          backgroundColor: 'transparent',
        };
      }
      if (variant === 'outline') {
        return {
          borderColor: theme?.subtleTextColor30,
          backgroundColor: 'white',
          color: theme?.subtleTextColor30,
        };
      }
      return {
        backgroundColor: theme?.subtleTextColor30,
        color: 'white',
      };
    }

    // Primary variant
    if (variant === 'primary') {
      return {
        backgroundColor: isHovered
          ? theme?.primaryBrandColor50
          : theme?.primaryBrandColor,
        color: 'white',
      };
    }

    // Secondary variant
    if (variant === 'secondary') {
      return {
        backgroundColor: isHovered
          ? theme?.secondaryBrandColor50
          : theme?.secondaryBrandColor,
        color: 'white',
      };
    }

    // Outline variant
    if (variant === 'outline') {
      return {
        borderColor: isHovered
          ? theme?.primaryBrandColor50
          : theme?.primaryBrandColor,
        color: theme?.primaryBrandColor,
        backgroundColor: isHovered ? theme?.backgroundColor : 'transparent',
      };
    }

    // Text variant
    if (variant === 'text') {
      return {
        backgroundColor: 'transparent',
        color: isHovered
          ? theme?.primaryBrandColor50
          : theme?.primaryBrandColor,
      };
    }

    // Default fallback
    return {
      backgroundColor: isHovered
        ? theme?.primaryBrandColor50
        : theme?.primaryBrandColor,
      color: 'white',
    };
  };

  const getButtonClassName = () => {
    const baseClasses =
      'flex cursor-pointer items-center justify-center rounded-md px-4 py-3 font-semibold transition-colors';
    const disabledClasses = isDisabled ? 'cursor-not-allowed opacity-70' : '';
    const outlineClasses = variant === 'outline' ? 'border' : '';

    return `${baseClasses} ${outlineClasses} ${disabledClasses} ${className}`.trim();
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      style={getButtonStyles()}
      className={getButtonClassName()}
      onMouseEnter={() => !isDisabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-busy={isLoading}
    >
      {isLoading ? (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
